<script setup>
import {reactive,onMounted,computed} from "vue";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {Permissions} from "@/Utils/Permissions";
import {ToastUtil} from "@/Utils/ToastUtil";

const props = defineProps({
  contextSchemaA: String,
  contextIdA: String
});

const data = reactive({
  valid: true,
  openBlockForm: false,
  dataLoading: true,
  loading: false,
  headers: [
    {
      title: 'Client',
      align: 'start',
      sortable: true,
      key: 'client',
    },
    { title: 'Date', key: 'created_date', sortable: false },
    { title: 'Created By', key: 'created_by', sortable: false },
    { title: 'Comment', key: 'comment', sortable: false },
    { title: 'Actions', key: 'actions', sortable: false },
  ],
  person: [],
  isEditing: false,
  clientList: [],
  items: [],
  editedItem: {
    client: '',
    date: '',
    createdBy: '',
    comment: ''
  },
  defaultItem: {
    client: '',
    date: '',
    createdBy: '',
    comment: ''
  },
});

const csvData = computed(()=>{
  // CSV File
  return data.person.map((item) => ({
    ...item,
    client: item.client,
    date: item.date,
    createdby: item.createdby,
    comment: item.comment
  }))
})

const csvExport = (arrData) => {
  // CSV File
  let csvContent = 'data:text/csv;charset=utf-8,'
  csvContent += [
    Object.keys(arrData[0]).join(';'),
    ...arrData.map((item) => Object.values(item).join(';')),
  ]
      .join('\n')
      .replace(/(^\[)|(\]$)/gm, '')

  const data = encodeURI(csvContent)
  const link = document.createElement('a')
  link.setAttribute('href', data)
  link.setAttribute('download', 'export.csv')
  link.click()
}

const listAll = () => {

  ApiWrapper.post('block-person', 'get-blocked-people', {
    personId: props.contextIdA
  })
      .then((response) => {
        if(response.items){
          data.items = response.items
        }
        if(response.clientNames){
          data.clientList = response.clientNames.map(function (obj) {
            return obj.name
          })
        }
      })
      .catch((e)=>{
        ToastUtil.error(e);
      })
      .finally(()=>{
        data.loading = false
        data.dataLoading = false
      })
}

const save = () => {
  data.loading = true
  data.openBlockForm = false
  if(data.isEditing){
    ApiWrapper.post('block-person', 'edit-block', {
          comment: data.editedItem.comment,
          blockid: data.editedItem.blockid
        })
        .then((response) => {
          listAll()
          cancel()

          ToastUtil.successMsg('Updated blocked person');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.loading = false;
          cancel();
        })
  }else{
    ApiWrapper.post('block-person', 'create-block', {
          editedItem: data.editedItem,
          personId: props.contextIdA
        })
        .then((response) => {
          listAll()
          cancel()

          ToastUtil.successMsg('Created a new person block');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.loading = false;
          cancel();
        })
  }

}

const deleteItem = (item) => {
  data.loading = true
  data.openBlockForm = false

  ApiWrapper.post('block-person', 'delete-block', {
        id: item.blockid,
        personId: props.contextIdA
      })
      .then((response) => {
        listAll()

        ToastUtil.successMsg('Removed person block');
      })
      .catch((err) => {
        ToastUtil.error(err);
        data.loading = false
        data.dataLoading = false
      })
}

const cancel = () => {
  data.openBlockForm = false
  data.isEditing = false
  data.editedItem = JSON.parse(JSON.stringify(data.defaultItem))
}

onMounted(()=>{
  listAll()
})
</script>

<template>
  <v-container fluid>
    <v-toolbar flat color="white">
      <v-toolbar-title>Blocks</v-toolbar-title>
      <v-progress-linear
          :active="data.loading"
          :indeterminate="data.loading"
          absolute
          bottom
          color="primary"
      >
      </v-progress-linear>
      <v-divider class="mx-4" inset vertical></v-divider>
      <v-icon @click="csvExport(csvData)" v-if="data.items.length"> mdi-file-download </v-icon>

      <v-spacer></v-spacer>

      <v-btn
          size="small"
          v-if="!data.openBlockForm && Permissions.checkPermissions(['blocks.create'])"
          class="primary"
          :disabled="data.loading || data.dataLoading"
          @click="data.openBlockForm = true"
      >
        New Block
      </v-btn>
    </v-toolbar>

    <!-- New Block Button -->
    <div v-if="data.openBlockForm">
      <v-card flat>
        <v-card-title v-if="!data.isEditing" class="primary--text"
        >New Block</v-card-title
        >
        <v-card-title class="primary--text"
        >Edit Block</v-card-title>

        <v-form ref="form" v-model="data.valid" lazy-validation>
          <v-row class="ml-2">
            <v-col cols="6">
              <v-autocomplete
                  :items="data.clientList"
                  label="Client"
                  clearable
                  v-model="data.editedItem.client"
                  v-if="!data.isEditing"
                  :rules="[v => !!v || 'Client is required']"
              >
              </v-autocomplete>
            </v-col>
          </v-row>
          <v-row class="ml-2">
            <v-col cols="6">
              <v-textarea
                  v-model="data.editedItem.comment"
                  label="Comment"
                  filled
                  clearable
                  auto-grow
                  :rules="[v => !!v || 'Comment is required']"
              ></v-textarea>
            </v-col>
          </v-row>
          <v-card-actions class="mb-2">
            <v-btn color="error" @click="cancel()">Cancel</v-btn>
            <v-btn color="primary" @click="save()" :disabled="!data.valid"
            >Submit</v-btn
            >
          </v-card-actions>
        </v-form>
      </v-card>
    </div>

    <v-data-table
        :headers="data.headers"
        :items="data.items"
        class="elevation-1"
        :loading="data.dataLoading"
        loading-text="Loading... Please wait"
    >
      <template v-slot:[`item.client`]="{ item }">
        <RouterLink :to="`/client/${item.clientid}`">
          {{ item.client}}
        </RouterLink>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-icon v-if="Permissions.checkPermissions(['blocks.update'])" class="mr-2" @click="data.editedItem = JSON.parse(JSON.stringify(item)); data.isEditing=true; data.openBlockForm=true"> mdi-pencil </v-icon>
        <v-icon v-if="Permissions.checkPermissions(['blocks.delete'])" @click="deleteItem(item)"> mdi-delete </v-icon>
      </template>
    </v-data-table>
  </v-container>
</template>

<style scoped>
[v-cloak] {
  display: none;
}

.container {
  padding: 5px;
}
</style>